import { useEffect, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { getAdyenMerchants, getAdyenMerchantStatement } from 'apis/adyenReportAPIs';
import ReportDateRange from 'components/ReportDateRange/ReportDateRange';
import compactFieldSx from 'components/ReportDateRange/compactFieldSx';
import { showErrorSnackbar } from 'store/actions/snackbarMessage';
import StatementLines from './components/StatementLines';
import StatementSummary from './components/StatementSummary';

// Admin only (the server enforces it): one merchant's Adyen statement, read live from Adyen's
// Transfers and Balance Account APIs, with OrderBuddy's commission joined from the webhook records.

const today = () => moment().format('YYYY-MM-DD');

// The Autocomplete sets its own input padding, which the theme's !important padding would override.
const merchantFieldSx = {
  ...compactFieldSx,
  width: '100% !important',
  '& .MuiOutlinedInput-root': { ...compactFieldSx['& .MuiOutlinedInput-root'], width: '100% !important' },
  '& .MuiOutlinedInput-input': { padding: '2.5px 4px 2.5px 8px !important' },
};

const AdyenReports = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [merchants, setMerchants] = useState([]);
  const [merchant, setMerchant] = useState(null);
  const [startDateStr, setStartDateStr] = useState(moment().subtract(6, 'days').format('YYYY-MM-DD'));
  const [endDateStr, setEndDateStr] = useState(today());
  const [statement, setStatement] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleError = (error, fallback) => {
    console.error(fallback, error);
    if (error.response && error.response.status === 403) {
      history.push('/sign-in');
      return;
    }
    const message = error.response && error.response.data && error.response.data.message;
    dispatch(showErrorSnackbar(message || fallback));
  };

  useEffect(() => {
    getAdyenMerchants()
      .then(setMerchants)
      .catch((error) => handleError(error, 'Cannot load Adyen merchants'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async () => {
    setIsLoading(true);
    try {
      setStatement(await getAdyenMerchantStatement(merchant.restaurantId, startDateStr, endDateStr));
    } catch (error) {
      handleError(error, 'Cannot load the Adyen statement');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>Adyen Reports</Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        A merchant&apos;s statement read live from Adyen: payments, refunds, chargebacks, fees and payouts booked to
        its balance account. Sydney time, up to 31 days.
      </Typography>

      <Stack direction="row" flexWrap="wrap" alignItems="center" gap={2} sx={{ my: 2 }}>
        <Autocomplete
          sx={{ minWidth: 300 }}
          size="small"
          options={merchants}
          value={merchant}
          onChange={(event, value) => {
            setMerchant(value);
            setStatement(null);
          }}
          getOptionLabel={(option) => `${option.name} (${option.restaurantId})`}
          isOptionEqualToValue={(option, value) => option.restaurantId === value.restaurantId}
          renderInput={(params) => <TextField {...params} label="Merchant" sx={merchantFieldSx} />}
        />
        <ReportDateRange
          startDateStr={startDateStr}
          endDateStr={endDateStr}
          onStartChange={setStartDateStr}
          onEndChange={setEndDateStr}
        />
        <Button variant="contained" color="primary" disabled={!merchant || isLoading} onClick={load}>
          Search
        </Button>
      </Stack>
      {isLoading && <LinearProgress />}

      {statement && (
        <>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {statement.restaurantName || statement.restaurantId} · {statement.balanceAccountId}
          </Typography>
          <StatementSummary statement={statement} />
          <StatementLines lines={statement.lines} />
        </>
      )}
    </Box>
  );
};

export default AdyenReports;
